import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DELIVERY_DETAIL_PARAM, DELIVERY_HIGHLIGHT_PARAM, STOCK_DETAIL_PARAM } from '@constants/logisticsParams';
import { useSelector } from 'react-redux';
import { queryAfterCriticalToggle } from '@d-orca/shared/client/logistics';
import { LogisticsNotFoundError, LogisticsSiteNotLinkedError } from '@apis/logistics';
import { type Delivery, type InventoryLevel } from '@interfaces/ILogistics';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { buildOrderPrefill } from './orderPrefill';
import { materialPrefillFor, openOrderNoticeFor, openStockFor, stockFormPlumbingFor, stockUnitFor } from './orderDialogDerivations';
import { soleMaterialOf } from './lines';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { PendingDeleteModal, type IPendingDelete } from './PendingDeleteModal';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { useHeldRecord } from '@hooks/useHeldRecord';
import { PageTour } from '@components/PageTour/PageTour';
import { useLogisticsTour } from './tour/useLogisticsTour';
import { LOGISTICS_TOUR_ID } from './tour/logisticsTour';
import { KpiCards } from './panels/KpiCards';
import { DeliveryBoard } from './panels/DeliveryBoard';
import { SiteMap } from './panels/SiteMap';
import { InventoryLevels } from './panels/InventoryLevels';
import { WasteTracking } from './panels/WasteTracking';
import { DeliveryHistory } from './panels/DeliveryHistory';
import { StockEntryDialog } from './panels/StockEntryDialog';
import { buildTrackedWiring } from './panels/oneOpenOrder';
import { DeliveryOrderDialog } from './panels/DeliveryOrderDialog';
import { DeliveryDetailDrawer } from './panels/DeliveryDetailDrawer';
import { DeliveryCalendar } from './calendar/DeliveryCalendar';
import { indexStockByName } from './stockActions';
import { createStockActions } from './logisticsStockActions';
import { StockDetailDrawer } from './panels/StockDetailDrawer';
import { LogisticsBanner } from './LogisticsBanner';
import { LogisticsSkeleton } from './LogisticsSkeleton';
import { LogisticsToastHost } from './LogisticsToastHost';
import { useLogisticsUrlState } from './useLogisticsUrlState';
import { useSiteDayRollover } from './useSiteDayRollover';
import { LogisticsEditLockProvider } from './revision/LogisticsEditLock';
import { useLogisticsReload } from './revision/useLogisticsReload';
import { filterDeliveries, filterLevels, filterUntracked, filterWaste } from './materialsSearch';
import { useLogisticsQueries } from './useLogisticsQueries';
import { useLogisticsMutations, type IOrderDialogState } from './useLogisticsMutations';
import {
  buildRowClickStockDialog,
  syncArrivals,
  buildDeliveredStockDialog,
  buildRenewArrivedStockDialog,
  type IStockDialogState,
} from './logisticsStockDialog';
import styles from './LogisticsPage.module.css';

/** The Materials search's URL key: `/logistics/<siteId>?q=<query>`. */
const MATERIALS_QUERY_PARAM = 'q';
/** The open detail drawer's URL key: `?delivery=<ref>`. */
const DETAIL_PARAM = DELIVERY_DETAIL_PARAM;
const STOCK_PARAM = STOCK_DETAIL_PARAM;
/** "Show me this card" from another page: highlight only, no drawer. */
const HIGHLIGHT_PARAM = DELIVERY_HIGHLIGHT_PARAM;
/** The pipeline holds every order until it is synced; a synced one is
 *  history (Delivery History below). `isSynced` is every line synced. */
const onBoard = (delivery: Delivery) => !delivery.isSynced;

export function LogisticsPage() {
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  /* The project's own timezone — every day-level reading on this page (which
     day stock was counted, whether a delivery is overdue, which day it landed)
     is a question about the SITE's calendar. Deliberately no `?? 'Asia/Tokyo'`
     fallback: guessing a zone silently mislabels dates for any site outside it,
     so the page waits for the real value instead. `useEnsureWorkingSite` keeps
     it in step with the `:siteId` in the URL. */
  const tz = useSelector(selectSiteTz);
  /* "Today" is read in render all over the page (overdue badges, due-day
     asks, the waste window); this re-renders it when the SITE's day turns. */
  useSiteDayRollover(tz);

  /**
   * URL-backed view state. Two keys, same discipline:
   *
   *   ?q=<query>       — the Materials segment's unified search. Filters VIEWS
   *                      only: every dialog, lookup, and KPI keeps reading the
   *                      full `data`.
   *   ?delivery=<ref>  — the open (read-only) detail drawer.
   *
   * A filtered view or an open drawer survives a refresh and travels as a
   * link. The query box itself types into local state mirrored to `?q=` (see
   * `useLogisticsUrlState` for why). `replace` keeps typing and drawer
   * toggles from burying the history under one entry per change.
   */
  const { searchParams, query: materialsQuery, setQuery: setMaterialsQuery, setUrlParam } =
    useLogisticsUrlState(MATERIALS_QUERY_PARAM);
  const searchActive = materialsQuery.trim().length > 0;
  /** Whether the Site Map panel is the fixed full-viewport overlay — the
   *  panel's own ⤢/⤡ toolbar button flips this; the page is what actually
   *  owns the state, since a panel has no say over its own slot in the
   *  page's layout. */
  const [mapExpanded, setMapExpanded] = useState(false);
  // `useSiteMapOverlay`'s Esc-precedence effect already reads this via an
  // Effect Event (`onEscape`), which sees the latest render's value without
  // needing it in the effect's own deps — the listener's resubscription
  // doesn't depend on this function's identity. The `useCallback` here is
  // just cheap hygiene for any future memoized consumer, not what's keeping
  // that effect from re-subscribing.
  const toggleMapExpanded = useCallback(() => setMapExpanded((v) => !v), []);
  const [stockDialog, setStockDialog] = useState<IStockDialogState | null>(null);
  const [orderDialog, setOrderDialog] = useState<IOrderDialogState>(null);
  const [deletingRef, setDeletingRef] = useState<string | null>(null);
  /**
   * A delivery a stock row asked to be shown.
   *
   * The nonce is the point: asking twice for the same delivery must scroll and
   * flash again, and a plain string would look unchanged the second time. Lives
   * on the page because the two panels are siblings — the stock line raises the
   * ref, the pipeline consumes it.
   */
  const [jumpTo, setJumpTo] = useState<{ ref: string; nonce: number } | null>(null);
  /** Both panels' deletes are irreversible and there is no undo, so their
   *  controls (each detail drawer's Edit tab) only stage a target here; the
   *  PendingDeleteModal is what actually fires. */
  const [pendingDelete, setPendingDelete] = useState<IPendingDelete>(null);
  const { toasts, push, dismiss } = useToasts();
  const { logisticsQuery, statusesQuery, suppliersQuery } = useLogisticsQueries(siteId);
  const { data, isLoading, isError, error, refetch } = logisticsQuery;
  // Folded-name lookup for the drawer's per-line stock actions — built once per payload.
  const stockIndex = useMemo(() => indexStockByName(data?.inventory ?? [], data?.untracked ?? []), [data?.inventory, data?.untracked]);
  // The pipeline's orders, stable per payload — the board memoizes its column counts on them.
  const boardDeliveries = useMemo(() => (data?.deliveries ?? []).filter(onBoard), [data?.deliveries]);
  const closeOrderDialog = useCallback(() => setOrderDialog(null), [setOrderDialog]);
  /** The board's header `+ New delivery` button — the action step's real
   *  control — is only rendered for a writer, alongside cards or a search.
   *  See `DeliveryBoard`. */
  const hasNewDeliveryButton = (data?.canEdit ?? false) && ((data?.deliveries.length ?? 0) > 0 || searchActive);
  /**
   * A link from ANOTHER page (the Scheduler's stock line) asking for one card
   * to be shown: turned into the same jump the in-page lines raise. Consumed
   * at render — the previous-value pattern, not an effect — and only ONCE THE
   * PAYLOAD IS IN: the board's jump reads a card the board has not rendered
   * yet, and silently does nothing. Fires once per distinct ref, so it never
   * fights an in-page jump. The param stays in the URL: it IS the deep link,
   * and a reload should land on the same card.
   */
  const highlightRef = searchParams.get(HIGHLIGHT_PARAM);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  if (data && highlightRef !== null && highlightRef !== highlighted) {
    setHighlighted(highlightRef);
    setJumpTo((previous) => ({ ref: highlightRef, nonce: (previous?.nonce ?? 0) + 1 }));
  }
  /**
   * The record each drawer's URL key names, or null (drawer closed). Derived
   * from the live list rather than held as an object: a ref that no longer
   * resolves — deleted, or a link pasted against another site — simply keeps
   * the drawer closed instead of showing a stale record. Derived HERE, above
   * the loading/error returns, because `useHeldRecord` is a hook and must
   * run on every render.
   */
  const detailDelivery = data?.deliveries.find((d) => d.ref === searchParams.get(DETAIL_PARAM)) ?? null;
  /* What each drawer RENDERS — the live record, held through the exit after
     a delete (`useHeldRecord`); actions keep reading the LIVE record, so
     nothing can act on one already gone. */
  const shownDelivery = useHeldRecord(detailDelivery);
  const detailLevel = data?.inventory.find((l) => l.materialName === searchParams.get(STOCK_PARAM)) ?? null;
  const shownLevel = useHeldRecord(detailLevel);
  const tour = useLogisticsTour({
    ready: data !== undefined,
    drawerOpen: detailDelivery !== null || detailLevel !== null,
    canEdit: data?.canEdit ?? false,
    hasNewDeliveryButton,
    closeOrderDialog,
  });
  /* Whether a drawer's Edit tab is open — it mounts the form on its first
     visit, so a glance at Overview alone holds no input. */
  const [deliveryEditOpen, setDeliveryEditOpen] = useState(false);
  const [stockEditOpen, setStockEditOpen] = useState(false);
  const { revision, stale, editLock, dock } = useLogisticsReload({
    siteId,
    loadedRevision: data?.revision,
    fetchedAfterMount: logisticsQuery.isFetchedAfterMount,
    canEdit: data?.canEdit ?? false,
    formOpen: orderDialog !== null || stockDialog !== null || deliveryEditOpen || stockEditOpen,
    push,
  });
  const {
    deleteMaterial,
    createOrder,
    patchOrder,
    deleteOrder,
    reorderColumns,
    createStatus,
    renameStatus,
    deleteStatus,
    handleMoveCard,
    handleToggleCritical,
  } = useLogisticsMutations({
    siteId,
    push,
    setOrderDialog,
    setDeletingRef,
    revision,
    /* An order just landed in Delivered — from a drag or from the edit form's
       own status field. Nothing is written to stock here: a delivery says what
       was ORDERED, and what is now on site is the old pile plus this one,
       which only the person standing there can total. So the arrival opens
       the same reconciliation the inventory row's Sync opens (prefilled with
       this delivery already counted) and asks.

       Never a gate: the move is already saved by the time this runs, so
       dismissing the dialog leaves the card where it was put. */
    onDelivered: (delivery: Delivery) => {
      if (!data) return;
      const next = buildDeliveredStockDialog(delivery, data.inventory, data.deliveries);
      if (next) setStockDialog(next);
    },
  });

  // `!tz` is part of the loading gate, not an error: the working site resolves
  // asynchronously, and rendering a date before it lands would mean picking a
  // zone at random.
  //
  // The column set is gated the same way, and for a stricter reason: the board
  // renders one column per status and `groupDeliveries` has nowhere to put a
  // card when the set is empty. Painting before it lands means a board with
  // every delivery silently missing from it.
  if (isLoading || statusesQuery.isLoading || !tz) {
    return <LogisticsSkeleton />;
  }
  if (error instanceof LogisticsNotFoundError) return <LogisticsBanner message={t('logistics.notFound')} />;
  if (error instanceof LogisticsSiteNotLinkedError) return <LogisticsBanner message={t('logistics.notLinked')} />;
  /* A dead column set is as fatal as dead deliveries, so it earns the same
     banner. With no statuses the board has no columns, `groupDeliveries` has
     nowhere to put a card, and the page would otherwise render KPI cards above
     an empty board body — every delivery silently gone, no error, nothing to
     retry. `logisticsQueryRetry` has already given up by the time this is true. */
  const failed = isError || !data || statusesQuery.isError;
  if (failed) {
    return (
      <LogisticsBanner
        /* The localized line only: the failure's own text is the API route
           and the server's English message, so it goes to the console. */
        message={t('logistics.error')}
        detail={isError || !data ? error : statusesQuery.error}
        retryLabel={t('logistics.retry')}
        /* Retries whichever half died — cheap, and the user does not know
           (or care) which of the two requests is the broken one. */
        onRetry={() => {
          void refetch();
          void statusesQuery.refetch();
        }}
      />
    );
  }
  // The server's answer on this caller's role (see `SiteLogistics.canEdit`).
  const { canEdit } = data;
  /* The stock form's inputs — one derivation shared by every surface that
     hosts `StockEntryForm` here (the stock dialog, the stock drawer, the site
     map's stock companion). */
  const stockPlumbing = stockFormPlumbingFor(siteId, data);

  /**
   * Saves an EDIT of an existing order — one path for both surfaces that
   *  offer it (the order dialog and the drawer's Edit tab), so the merge
   *  plumbing cannot fork. The form already decided whether this write
   *  carries a column move (which is also how an arrival is recorded). */
  const submitOrderEdit = (
    initial: Delivery,
    body: Parameters<typeof patchOrder.mutate>[0]['patch'],
    merge?: { remaining: number },
    /** Runs after the write lands — the drawer passes "close me", the dialog
     *  needs nothing (the mutation's own onSuccess already closes it). */
    onSaved?: () => void,
  ) => {
    /* The merge is a one-material move (the form only offers it on a
       single-line order), so the level and the arrived amount both come from
       that one line. */
    const soleLine = body.lines?.length === 1 ? body.lines[0]! : null;
    const level = soleLine ? openStockFor(data.inventory, soleLine.itemName) : null;
    patchOrder.mutate(
      {
        ref: initial.ref,
        patch: body,
        /* Carried so the write can tell a move INTO Delivered from a save on
           an order that was already there — only the first is an arrival. */
        initial,
        ...(merge && level && soleLine?.quantity != null
          ? { merge: { remaining: merge.remaining, level, arrived: soleLine.quantity } }
          : {}),
      },
      { onSuccess: onSaved },
    );
  };

  const toggleCritical = (delivery: Delivery) => {
    setMaterialsQuery(queryAfterCriticalToggle(data.deliveries, delivery, materialsQuery));
    handleToggleCritical(delivery, () => setMaterialsQuery(materialsQuery));
  };
  const shownDeliveries = filterDeliveries(boardDeliveries, materialsQuery);
  const shownInventory = filterLevels(data.inventory, materialsQuery);
  const shownUntracked = filterUntracked(data.untracked, materialsQuery);
  const { syncStock, addStock, lineAction, countInOtherUnit, countInOtherUnitFrom } = createStockActions({ data, stockIndex, tz, canEdit, setStockDialog });
  const unitOf = stockUnitFor(data.inventory);
  const shownWaste = filterWaste(data.waste, materialsQuery);

  return (
    <LogisticsEditLockProvider value={editLock}><LogisticsToastHost push={push}>
      <div className={styles.page}>
        {dock}

        {/* Reads the FULL data, like every other KPI: the tile states what the
            site is doing, not what the current search happens to show. Its
            reasons write the search rather than obeying it. */}
        <KpiCards
          kpis={data.kpis}
          inventory={data.inventory}
          untracked={data.untracked}
          deliveries={data.deliveries}
          tz={tz}
          materialsQuery={materialsQuery}
          onFilterMaterials={setMaterialsQuery}
        />

        {/* The Materials segment: the delivery board, the inventory list, and
            the waste list under ONE search. The frame (and only the frame) is
            what scopes the search — without it, a lone search pill would read
            as page-wide. */}
        <section className={styles.segment} aria-label={t('logistics.materials.title')}>
          <div className={styles.segmentHead}>
            <div>
              <p className={styles.segmentEyebrow}>{t('logistics.materials.title')}</p>
              <p className={styles.segmentSub}>{t('logistics.materials.subtitle')}</p>
            </div>
            <div className={styles.segmentSearch} data-tour="logistics-search">
              <SearchInput
                value={materialsQuery}
                onChange={setMaterialsQuery}
                placeholder={t('logistics.materials.searchPlaceholder')}
                ariaLabelClear={t('logistics.materials.searchClear')}
              />
            </div>
          </div>

          {/* Full width: the board scrolls horizontally through its columns, and
              half a page is not enough to see more than two of them. */}
          <div className={styles.rowFull}>
            <DeliveryBoard
              deliveries={shownDeliveries}
              allDeliveries={boardDeliveries}
              statuses={statusesQuery.data ?? []}
              onCreate={() => setOrderDialog({ initial: null })}
              onOpenDetail={(delivery: Delivery) => setUrlParam(DETAIL_PARAM, delivery.ref)}
              onMoveCard={handleMoveCard}
              onToggleCritical={toggleCritical}
              onReorderColumns={(order) => reorderColumns.mutate(order)}
              onCreateStatus={(name) => createStatus.mutate(name)}
              onRenameStatus={(statusId, name) => renameStatus.mutate({ statusId, name })}
              onDeleteStatus={(statusId) => deleteStatus.mutate(statusId)}
              statusesBusy={createStatus.isPending || renameStatus.isPending || deleteStatus.isPending}
              deletingRef={deletingRef}
              jumpTo={jumpTo}
              tz={tz}
              searchActive={searchActive}
              canEdit={canEdit}
              suppliers={suppliersQuery.data ?? []}
            />
          </div>

          <div className={`${styles.row} ${styles.rowEqual}`}>
            <InventoryLevels
              inventory={shownInventory}
              deliveries={data.deliveries}
              onAdd={() => setStockDialog({ prefill: null })}
              onOpenDetail={(level: InventoryLevel) => setUrlParam(STOCK_PARAM, level.materialName)}
              // Renewing never writes directly — Delivery carries no quantity, and
              // leftover stock merged into the new pile means only the person
              // standing there knows the real total. Opens the stock dialog
              // prefilled instead (spec §4). See `buildRenewArrivedStockDialog` for
              // why the merge and the deferred `deliveredOnSave` write are shaped
              // the way they are.
              onRenewArrived={(level: InventoryLevel) =>
                setStockDialog(buildRenewArrivedStockDialog(level, data.deliveries))
              }
              onSync={syncStock}
              /* Past the reorder point: jump straight to a delivery order for this
                 material, seeded with everything the page can honestly infer.
                 `initial` stays null so the dialog is a CREATE — passing a partial
                 there would make the parent try to PATCH a missing ref. */
              onOrderNow={(level: InventoryLevel) =>
                setOrderDialog({
                  initial: null,
                  prefill: buildOrderPrefill(level, data.deliveries, tz, t),
                })
              }
              /* Bump the nonce every time, so asking for the same delivery twice
                 replays the scroll and the flash instead of reading as no change. */
              onJumpToDelivery={(deliveryRef: string) =>
                setJumpTo((prev) => ({ ref: deliveryRef, nonce: (prev?.nonce ?? 0) + 1 }))
              }
              untracked={shownUntracked}
              onAddStock={addStock}
              deletingMaterial={deleteMaterial.isPending ? (deleteMaterial.variables ?? null) : null}
              siteId={siteId}
              tz={tz}
              searchActive={searchActive}
              canEdit={canEdit}
            />
            <WasteTracking waste={shownWaste} tz={tz} searchActive={searchActive} />
          </div>
        </section>

        {/* Site Map spans the full row on its own — no sibling panel to share
            it with. The panel's own toolbar owns the expand affordance:
            `expanded` makes `<SiteMap>`'s card a FIXED full-viewport overlay
            (see SiteMap.module.css's `.card[data-expanded]`), which needs no
            help from this slot's own layout to "widen" (a `position: fixed`
            descendant ignores its ancestor's placement entirely). The slot
            stays a plain wrapper — kept only for the stable testid and as the
            seam LogisticsPage still owns the `expanded` state through. */}
        <div className={styles.rowFull} data-testid="site-map-slot" data-tour="logistics-site-map">
          <SiteMap
            siteId={siteId}
            levels={data.inventory}
            tz={tz}
            onError={(message) => push({ kind: 'error', title: message })}
            expanded={mapExpanded}
            onToggleExpanded={toggleMapExpanded}
            /* The stock companion's Edit tab renders the same StockEntryForm —
               identical plumbing to the stock dialog/drawer below. */
            stockPlumbing={stockPlumbing}
            onDeleteStock={(level: InventoryLevel) => setPendingDelete({ kind: 'material', level })}
            canEdit={canEdit}
          />
        </div>

        {/* Synced orders, off the board: settled history, with its own search
            (the Materials search above does not reach it). */}
        <div className={styles.rowFull}>
          <DeliveryHistory deliveries={data.deliveries} suppliers={suppliersQuery.data ?? []} tz={tz} />
        </div>

        <StockEntryDialog
          {...stockPlumbing}
          open={stockDialog !== null}
          prefill={stockDialog?.prefill ?? null}
          mode={stockDialog?.mode}
          arrived={stockDialog?.arrived ?? null}
          estimatedRemaining={stockDialog?.estimatedRemaining}
          /* The arrival a Sync records rides the count's own request: the server
             checks the count, moves the order into Delivered, then writes the
             count — so a refused count leaves no order arrived with nothing
             reconciled, and the arrival still precedes the count it merges into
             (see `IStockEntryFormProps.arrivals`). Only a confirmed Save sends it. */
          arrivals={stockDialog ? syncArrivals(stockDialog) : undefined}
          unitMismatchActions={{
            onOpenOrder: (ref) => { setStockDialog(null); setUrlParam(DETAIL_PARAM, ref); },
            onCountInOtherUnit: countInOtherUnit,
          }}
          {...buildTrackedWiring({ inventory: data.inventory, onOpenLevel: (l) => setStockDialog(buildRowClickStockDialog(l as InventoryLevel)) })}
          onClose={() => setStockDialog(null)}
        />
        {/* Mounted only while open — the dialog reads `initial` once on mount,
            so each open must be a fresh mount. */}
        {orderDialog ? (
          <DeliveryOrderDialog
            suppliers={suppliersQuery.data ?? []}
            saveError={orderDialog.initial ? patchOrder.error : createOrder.error}
            tz={tz}
            suggestions={data.materialSuggestions}
            materialPrefill={materialPrefillFor(data, tz, t)}
            stockUnitOf={unitOf}
            onCountInOtherUnit={countInOtherUnitFrom(() => setOrderDialog(null))}
            deliveries={data.deliveries}
            initial={orderDialog.initial}
            /* excludeRef future-proofs the currently-unreached edit path. */
            openOrderNotice={openOrderNoticeFor(data.deliveries, orderDialog.initial?.ref)}
            onViewOrder={(ref) => { setOrderDialog(null); setJumpTo((prev) => ({ ref, nonce: (prev?.nonce ?? 0) + 1 })); }}
            boardStatuses={statusesQuery.data ?? []}
            prefills={orderDialog.prefill && [orderDialog.prefill]}
            /* The running stock for this delivery's material, so the dialog can
               offer the merge when the status moves to Delivered. */
            openStock={openStockFor(
              data.inventory,
              orderDialog.initial
                ? soleMaterialOf(orderDialog.initial)
                : orderDialog.prefill?.itemName,
            )}
            onSubmit={(body, merge) => {
              const initial = orderDialog.initial;
              if (!initial) {
                /* No column key exists on a create — the dialog offers no column
                   there. Stripped rather than assumed: `postDeliveryOrder` types
                   it as `never`, so this destructure is the compiler checking
                   that promise instead of a comment making it. */
                const { status: _status, ...create } = body;
                createOrder.mutate(create);
                return;
              }
              submitOrderEdit(initial, body, merge);
            }}
            onClose={() => setOrderDialog(null)}
            submitting={createOrder.isPending || patchOrder.isPending}
          />
        ) : null}
        {/* Edit and delete moved off the card kebab into the drawer's Edit tab
            (2026-08-20). Always mounted: `open` follows the ?delivery= param and
            Base UI plays the exit itself, rendering the HELD record so a deleted
            one still has something to slide out. A confirmed delete just clears
            the param — no close choreography to run. */}
        <DeliveryDetailDrawer
          siteId={siteId}
          delivery={shownDelivery}
          open={detailDelivery !== null}
          boardStatuses={statusesQuery.data ?? []}
          suppliers={suppliersQuery.data ?? []}
          onClose={() => setUrlParam(DETAIL_PARAM, null)}
          tz={tz}
          suggestions={data.materialSuggestions}
          materialPrefill={materialPrefillFor(data, tz, t)}
          openStock={detailDelivery ? openStockFor(data.inventory, soleMaterialOf(detailDelivery)) : null}
          stockUnitOf={unitOf}
          onCountInOtherUnit={countInOtherUnitFrom(() => setUrlParam(DETAIL_PARAM, null))}
          onSubmit={(body, merge) => {
            // A landed save closes the drawer — the record is written, the
            // board is where it shows. A failed save leaves the drawer (and the
            // typed values) in place.
            if (detailDelivery) {
              submitOrderEdit(detailDelivery, body, merge, () => setUrlParam(DETAIL_PARAM, null));
            }
          }}
          submitting={patchOrder.isPending}
          saveError={patchOrder.error}
          onDelete={(delivery: Delivery) => setPendingDelete({ kind: 'delivery', delivery })}
          onToggleCritical={toggleCritical}
          lineAction={lineAction}
          canEdit={canEdit}
          onEditVisitedChange={setDeliveryEditOpen}
        />

        {/* The stock rows' counterpart — edit and delete moved off the row
            kebab into this drawer's Edit tab, the same shape (and the same
            held-record close choreography) as the delivery drawer above. */}
        <StockDetailDrawer
          level={shownLevel}
          open={detailLevel !== null}
          onClose={() => setUrlParam(STOCK_PARAM, null)}
          tz={tz}
          onDelete={(level: InventoryLevel) => setPendingDelete({ kind: 'material', level })}
          {...stockPlumbing}
          canEdit={canEdit}
          onEditVisitedChange={setStockEditOpen}
        />

        <PendingDeleteModal
          pending={pendingDelete}
          loading={stale}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            if (pendingDelete?.kind === 'delivery') {
              deleteOrder.mutate(pendingDelete.delivery.ref);
              /* The record under deletion is the open drawer's (the Edit tab is
                 where deletes are staged) — drop the param so the drawer closes
                 (Base UI slides out the held record) instead of the refetch
                 leaving `?delivery=` dangling at a ref that no longer
                 resolves. */
              if (detailDelivery?.ref === pendingDelete.delivery.ref) setUrlParam(DETAIL_PARAM, null);
            }
            if (pendingDelete?.kind === 'material') {
              deleteMaterial.mutate(pendingDelete.level.materialName);
              /* Same as the delivery above: the staging surface is the open
                 drawer's Edit tab, so drop the param and let the held record
                 slide out. */
              if (detailLevel?.materialName === pendingDelete.level.materialName) {
                setUrlParam(STOCK_PARAM, null);
              }
            }
            setPendingDelete(null);
          }}
        />
        <DeliveryCalendar
          key={siteId} deliveries={data.deliveries} statuses={statusesQuery.data ?? []} suppliers={suppliersQuery.data ?? []} tz={tz} escapeTaken={Boolean(detailDelivery || detailLevel || orderDialog || stockDialog || pendingDelete || mapExpanded || tour.open)} onOpenDelivery={(delivery) => setUrlParam(DETAIL_PARAM, delivery.ref)}
        />
        <PageTour
          key={tour.key}
          tourId={LOGISTICS_TOUR_ID}
          steps={tour.steps}
          open={tour.open}
          context={tour.context}
          onDismiss={tour.dismiss}
        />
        {createPortal(<Toasts toasts={toasts} onDismiss={dismiss} />, document.body)}
      </div>
    </LogisticsToastHost></LogisticsEditLockProvider>
  );
}
